<template>
  <div class="w-full h-full flex flex-col sm:p-2 relative">
    <div
      class="w-full flex items-center gap-2 sm:mb-2 h-14 sm:h-auto p-2 sm:p-0 bg-n-900 sm:bg-transparent border-t border-n-800 sm:border-none shrink-0"
      :class="{
        'pl-28 sm:pl-0': hasReservationSubscription
      }"
    >
      <l-date-picker
        v-if="!tabId"
        v-model:date="selectedDate"
        :show-time="false"
        :only-future="true"
        :show-arrows="true"
        size="small"
        class="sm:max-w-56 w-full"
      >
        <template #default="{ date: actualDate }">
          {{
            !actualDate || isToday(actualDate)
              ? t('reservations.today')
              : format(actualDate, 'dd/MM/yyyy')
          }}
        </template>
      </l-date-picker>
      <l-search-bar
        v-if="!isMobile"
        v-model="searchQuery"
        size="small"
        :placeholder="t('reservations.search')"
      />
      <l-button
        v-if="!tabId"
        class="shrink-0"
        icon="plus"
        size="small"
        @click="editingReservation = {}"
      >
        <template #default v-if="!isMobile">
          {{ t('reservations.add') }}
        </template>
      </l-button>
    </div>
    <div class="flex flex-col w-full h-full overflow-y-scroll scrolling-touch p-2 pb-14">
      <div
        class="flex flex-col"
        v-for="[time, group] in groupReservation"
        :id="time"
        :key="time"
        :class="{
          'opacity-30': isBefore(addHours(new Date(time), 1), now)
        }"
      >
        <div
          class="h-10 text-n-0 flex items-center gap-x-1 border-b border-n-600 mt-4 heading-3"
        >
          {{ format(time, 'HH:mm') }}
        </div>
        <reservation-row
          v-for="reservation in group"
          :key="reservation.id"
          :disabled="!!tabId && !!reservation.tabId"
          :highlight="reservationId === reservation.id"
          :reservation="reservation"
          :time="now"
          @click="selectReservation(reservation)"
        />
      </div>
      <l-empty-case
        v-if="Object.keys(groupReservation).length === 0"
        class="flex-1 align-center justify-center"
        type="empty"
      >
        {{ t('reservations.empty') }}
      </l-empty-case>
    </div>
    <Transition name="slide">
      <preview-reservations
        v-if="viewReservations"
        :reservation="viewReservations"
        @edit="selectForEdit()"
        @close="selectedReservationId = undefined"
      />
    </Transition>
    <Transition name="slide">
      <reservation-form
        v-if="editingReservation"
        :reservation="editingReservation"
        @close="editingReservation = undefined"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useNow } from '@vueuse/core'
import { useFuse } from '@vueuse/integrations/useFuse'
import {
  addHours,
  addMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  roundToNearestHours
} from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onActivated, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LButton, LDatePicker, LEmptyCase, LSearchBar } from '@last/core-ui/paprika'

import { useNotifications } from '@/composables/useNotifications'
import { useAuthStore } from '@/store/auth'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Reservation } from '@/types'
import { isMobile } from '@/utils/device-type'

import ReservationForm from './form/ReservationForm.vue'
import PreviewReservations from './PreviewReservations.vue'
import ReservationRow from './ReservationRow.vue'

const reservationsStore = useReservationsStore()
const tabsStore = useTabsStore()
const authStore = useAuthStore()

const { editReservation } = reservationsStore
const { reservations, sortedReservations } = storeToRefs(reservationsStore)
const { hasReservationSubscription } = storeToRefs(authStore)

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { notifyInfo } = useNotifications()

const editingReservation = ref()
const searchQuery = ref('')
const selectedReservationId = ref<string>()
const selectedDate = ref<Date | null>(null)

const viewReservations = computed<Reservation | undefined>(() =>
  selectedReservationId.value ? reservations.value[selectedReservationId.value] : undefined
)

const tabId = computed(() => route.query.tabId as string | null)
const reservationId = computed(() => route.query.reservationId as string | null)

onActivated(() => {
  if (route.query.editReservation) {
    editingReservation.value = reservationsStore.reservations[route.query.editReservation as string]
    router.replace({ name: 'reservations' })
  }
  focus()
})

const now = useNow({
  interval: 60_000
})

function focus() {
  const nextReservationTime = Object.keys(groupReservation.value).find(time =>
    isAfter(addHours(new Date(time), 1), now.value)
  )
  if (nextReservationTime) {
    const el = document.getElementById(nextReservationTime)
    el?.scrollIntoView()
  }
}

watch(reservationId, () => {
  if (reservationId.value) {
    const reservation = reservations.value[reservationId.value]
    if (reservation && !isSameDay(reservation.dateTime, selectedDate.value ?? now.value)) {
      selectedDate.value = new Date(reservation.dateTime)
      nextTick(() => {
        const el = document.getElementById(reservationId.value!)
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
  }
})

function selectReservation(reservation: Reservation) {
  if (tabId.value) {
    linkReservation(reservation.id)
  } else {
    selectedReservationId.value = reservation.id
  }
}

function linkReservation(reservationId: string) {
  if (!tabId.value) return

  const tab = tabsStore.tabs[tabId.value]
  if (!tab) return

  const tabIdAlreadyLinked = Object.values(sortedReservations.value).find(
    reservation => reservation.tabId === tabId.value
  )
  if (tabIdAlreadyLinked) {
    const oldReservation = {
      ...tabIdAlreadyLinked,
      tabId: null,
      status: 'confirmed'
    } as const
    editReservation(oldReservation)
  }
  const reservation = {
    ...reservations.value[reservationId],
    tabId: tabId.value,
    tables: tab.tables,
    status: 'ongoing'
  } as const
  editReservation(reservation)
  notifyInfo({
    title: t('link-reservation.reservation-linked')
  })
  router.back()
}

const availableReservations = computed(() => {
  const reservations = sortedReservations.value
  const date = selectedDate.value ?? now.value
  return reservations.filter(reservation => isSameDay(reservation.dateTime, date))
})

const { results: searchResults } = useFuse(searchQuery, availableReservations, {
  matchAllWhenSearchEmpty: true,
  fuseOptions: {
    keys: ['name', 'surname', 'phoneNumber'],
    threshold: 0.4,
    minMatchCharLength: 1
  }
})

const groupReservation = computed(() => {
  const grouped = {} as Record<string, Reservation[]>

  for (const { item: reservation } of searchResults.value) {
    const groupKey = roundToNearestHours(reservation.dateTime, {
      roundingMethod: 'trunc'
    }).toISOString()
    ;(grouped[groupKey] ||= []).push(reservation)
  }

  Object.keys(grouped).forEach(key => {
    const reservations = grouped[key]
    const firstHalf = reservations.filter(r => new Date(r.dateTime).getMinutes() < 30)
    const secondHalf = reservations.filter(r => new Date(r.dateTime).getMinutes() >= 30)

    if (firstHalf.length >= 3 && secondHalf.length >= 3) {
      grouped[key] = firstHalf
      grouped[addMinutes(new Date(key), 30).toISOString()] = secondHalf
    }
  })

  return Object.entries(grouped).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
})

function selectForEdit() {
  editingReservation.value = viewReservations.value
  selectedReservationId.value = undefined
}
</script>
